// Consolidated Speech Middleware - Removes duplicate transcript callbacks
import type { Middleware } from "@reduxjs/toolkit";
import { deepgramService } from "../../services/deepgram";
import type { RootState } from "../types";
import * as speechActions from "../slices/speechSlice";
import type { ExchangeTimingMetadata } from "../../services/exchangeTiming";

interface SpeechAction {
  type: string;
  payload?: any;
}

let hasTriggeredInterruptionThisSession = false;
let speechInputStartedAt: number | null = null;
let speechTranscriptionStartedAt: number | null = null;
let speechTranscriptionCompletedAt: number | null = null;
let speechSpeculationTranscript = "";
let speechSpeculationClientId: string | null = null;
let speechSpeculationLastSentAt = 0;
let speechSpeculationTimer: ReturnType<typeof setTimeout> | null = null;
const speechStartWarmupLastSentAtBySession = new Map<string, number>();

const ENABLE_LLM1_SPEECH_SPECULATION =
  import.meta.env.VITE_ENABLE_LLM1_SPEECH_SPECULATION === "true" ||
  import.meta.env.DEV;
const ENABLE_LLM_SPEECH_START_WARMUP =
  import.meta.env.VITE_ENABLE_LLM_SPEECH_START_WARMUP !== "false";
const SPEECH_SPECULATION_MIN_CHARS = 18;
const SPEECH_SPECULATION_MIN_WORDS = 3;
const SPEECH_SPECULATION_RESTART_MIN_CHARS = 16;
const SPEECH_SPECULATION_MIN_SEND_INTERVAL_MS = 600;
const SPEECH_SPECULATION_DEBOUNCE_MS = 250;
const SPEECH_START_WARMUP_MIN_SEND_INTERVAL_MS = 15_000;

function resetSpeechExchangeTiming(): void {
  speechInputStartedAt = null;
  speechTranscriptionStartedAt = null;
  speechTranscriptionCompletedAt = null;
}

function normalizeSpeculativeTranscript(transcript: string): string {
  return transcript
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function clearSpeechSpeculationTimer(): void {
  if (speechSpeculationTimer) {
    clearTimeout(speechSpeculationTimer);
    speechSpeculationTimer = null;
  }
}

function resetSpeechSpeculation(store?: any, reason = "reset"): void {
  clearSpeechSpeculationTimer();

  const state = store?.getState?.() as RootState | undefined;
  const sessionId = state?.sessions.current?.sessionId;
  if (speechSpeculationTranscript && sessionId) {
    store.dispatch({
      type: "websocket/sendMessage",
      payload: {
        event: "llm1-speech-speculation-cancel",
        data: { sessionId, reason },
      },
    });
  }

  speechSpeculationTranscript = "";
  speechSpeculationClientId = null;
  speechSpeculationLastSentAt = 0;
}

function clearLocalSpeechSpeculation(): void {
  clearSpeechSpeculationTimer();
  speechSpeculationTranscript = "";
  speechSpeculationClientId = null;
  speechSpeculationLastSentAt = 0;
}

function maybeSendSpeechStartWarmup(store: any): void {
  if (!ENABLE_LLM_SPEECH_START_WARMUP) {
    return;
  }

  const state = store.getState() as RootState;
  const currentSession = state.sessions.current;
  if (
    !currentSession ||
    state.messages.isProcessing ||
    state.speech.isCollectingInterruption
  ) {
    return;
  }

  const nowMs = performance.now();
  const lastSentAt =
    speechStartWarmupLastSentAtBySession.get(currentSession.sessionId) ?? 0;
  if (nowMs - lastSentAt < SPEECH_START_WARMUP_MIN_SEND_INTERVAL_MS) {
    return;
  }

  speechStartWarmupLastSentAtBySession.set(currentSession.sessionId, nowMs);
  store.dispatch({
    type: "websocket/sendMessage",
    payload: {
      event: "llm-speech-start-warmup",
      data: {
        sessionId: currentSession.sessionId,
        caseId: currentSession.caseId,
      },
    },
  });
}

function maybeStartSpeechSpeculation(
  store: any,
  transcript: string,
  isFinal: boolean,
): void {
  if (!ENABLE_LLM1_SPEECH_SPECULATION) {
    return;
  }

  const state = store.getState() as RootState;
  const currentSession = state.sessions.current;
  const cleanTranscript = transcript.trim();
  const candidateTranscript = [...state.speech.transcriptQueue, cleanTranscript]
    .join(" ")
    .trim();
  const normalizedCandidate =
    normalizeSpeculativeTranscript(candidateTranscript);
  const normalizedPrevious = normalizeSpeculativeTranscript(
    speechSpeculationTranscript,
  );
  const wordCount = normalizedCandidate
    .split(/\s+/)
    .filter((word) => word.length > 0).length;

  if (
    !currentSession ||
    state.messages.isProcessing ||
    state.speech.isCollectingInterruption ||
    (state.avatar.isSpeaking && state.avatar.isConnected) ||
    candidateTranscript.length < SPEECH_SPECULATION_MIN_CHARS ||
    wordCount < SPEECH_SPECULATION_MIN_WORDS
  ) {
    return;
  }

  if (normalizedCandidate === normalizedPrevious) {
    return;
  }

  const isRevision =
    normalizedPrevious.length > 0 &&
    !normalizedCandidate.startsWith(normalizedPrevious);
  const grewEnough =
    candidateTranscript.length - speechSpeculationTranscript.length >=
    SPEECH_SPECULATION_RESTART_MIN_CHARS;

  if (speechSpeculationTranscript && !isFinal && !isRevision && !grewEnough) {
    return;
  }

  const sendSpeculation = () => {
    const latestState = store.getState() as RootState;
    const latestSession = latestState.sessions.current;
    if (!latestSession || latestState.messages.isProcessing) {
      return;
    }

    speechSpeculationClientId = `${latestSession.sessionId}-${Date.now()}`;
    speechSpeculationTranscript = candidateTranscript;
    speechSpeculationLastSentAt = performance.now();

    store.dispatch({
      type: "websocket/sendMessage",
      payload: {
        event: "llm1-speech-speculation-start",
        data: {
          sessionId: latestSession.sessionId,
          caseId: latestSession.caseId,
          transcript: candidateTranscript,
          clientSpeculationId: speechSpeculationClientId,
        },
      },
    });
  };

  clearSpeechSpeculationTimer();

  const elapsedSinceLastSend = performance.now() - speechSpeculationLastSentAt;
  if (
    isFinal ||
    elapsedSinceLastSend >= SPEECH_SPECULATION_MIN_SEND_INTERVAL_MS
  ) {
    sendSpeculation();
    return;
  }

  speechSpeculationTimer = setTimeout(
    sendSpeculation,
    Math.max(
      SPEECH_SPECULATION_DEBOUNCE_MS,
      SPEECH_SPECULATION_MIN_SEND_INTERVAL_MS - elapsedSinceLastSend,
    ),
  );
}

function buildSpeechExchangeTiming(
  inputCompletedAt: number,
): ExchangeTimingMetadata {
  const startedAt = speechInputStartedAt ?? inputCompletedAt;

  return {
    inputMode: "speech",
    inputStartedAt: startedAt,
    inputCompletedAt,
    transcriptionStartedAt: speechTranscriptionStartedAt ?? startedAt,
    transcriptionCompletedAt:
      speechTranscriptionCompletedAt ?? inputCompletedAt,
  };
}

export const speechMiddleware: Middleware<{}, RootState> =
  (store) => (next) => (action) => {
    const { type } = action as SpeechAction;

    // Always call next first
    const result = next(action);

    // Handle device loading
    if (type === "speech/loadDevices") {
      console.log("🎤 Loading audio devices");
      store.dispatch(speechActions.setLoadingDevices(true));

      // Get available devices
      navigator.mediaDevices
        .enumerateDevices()
        .then((devices) => {
          const audioDevices = devices.filter(
            (device) => device.kind === "audioinput",
          );
          console.log("🎤 Found audio devices:", audioDevices);
          store.dispatch(speechActions.setAvailableDevices(audioDevices));
        })
        .catch((error) => {
          console.error("🎤 Failed to load devices:", error);
          store.dispatch(
            speechActions.setError("Failed to load microphone devices"),
          );
          store.dispatch(speechActions.setLoadingDevices(false));
        });

      return result;
    }

    // Handle device selection changes
    if (type === "speech/setSelectedDevice") {
      const state = store.getState();
      console.log("🎤 Device changed, reconnecting if listening...");

      // If currently listening, restart connection with new device
      if (state.speech.isListening) {
        console.log("🎤 Restarting speech with new device");
        resetSpeechSpeculation(store, "device_changed");
        store.dispatch(speechActions.setListening(false));
        deepgramService.disconnect();

        // Restart after a brief delay
        setTimeout(() => {
          store.dispatch({ type: "speech/connectSpeech" });
        }, 500);
      }

      return result;
    }

    // Handle disconnect speech action
    if (type === "speech/disconnectSpeech") {
      console.log("🎤 Speech disconnectSpeech");
      resetSpeechSpeculation(store, "speech_disconnected");
      store.dispatch(speechActions.setListening(false));
      deepgramService.disconnect();
      return result;
    }

    // Handle connect speech action (with interim-based interruption)
    if (type === "speech/connectSpeech") {
      console.log("🎤 Speech connectSpeech");

      const state = store.getState();

      // Block if not enabled or connecting
      if (!state.speech.isEnabled || state.speech.isConnecting) {
        console.log("🎤 Connect blocked");
        return result;
      }

      // Start connection asynchronously
      setTimeout(async () => {
        try {
          console.log("🎤 Connecting to Deepgram...");

          const currentState = store.getState();
          await deepgramService.connect(
            {
              model: "nova-3",
              punctuate: false,
              vad_events: true,
            },
            // MAIN TRANSCRIPT CALLBACK - Interim-based interruption logic
            (transcript: string, isFinal: boolean) => {
              const transcriptReceivedAt = performance.now();
              if (speechInputStartedAt === null) {
                speechInputStartedAt = transcriptReceivedAt;
                speechTranscriptionStartedAt = transcriptReceivedAt;
              }
              if (isFinal) {
                speechTranscriptionCompletedAt = transcriptReceivedAt;
              }

              const debugState = store.getState();
              console.log("🎤 Transcript callback:", {
                transcript,
                isFinal,
                length: transcript.trim().length,
                isProcessing: debugState.messages.isProcessing,
                isCollectingInterruption:
                  debugState.speech.isCollectingInterruption,
                isInterrupted: debugState.messages.isInterrupted,
                avatarIsSpeaking: debugState.avatar.isSpeaking,
                avatarIsConnected: debugState.avatar.isConnected,
                hasTriggeredInterruptionThisSession,
              });

              store.dispatch(
                speechActions.transcriptReceived({
                  transcript,
                  isFinal,
                }),
              );

              maybeStartSpeechSpeculation(store, transcript, isFinal);

              // INTERIM-BASED INTERRUPTION TRIGGER (1+ characters, any result type)
              if (
                transcript.trim().length >= 1 &&
                !hasTriggeredInterruptionThisSession
              ) {
                const currentState = store.getState();
                console.log("1- checkif should interupt: ", Date.now());

                // Check interruption conditions
                const isLLMProcessing = currentState.messages.isProcessing;
                const isAvatarSpeaking =
                  currentState.avatar.isSpeaking &&
                  currentState.avatar.isConnected;
                const isAlreadyInterrupting =
                  currentState.speech.isCollectingInterruption;
                const isAlreadyInterrupted =
                  currentState.messages.isInterrupted;

                const shouldInterrupt =
                  (isLLMProcessing || isAvatarSpeaking) &&
                  !isAlreadyInterrupting &&
                  !isAlreadyInterrupted;

                if (shouldInterrupt) {
                  console.log("2- interruption triggered: ", Date.now());
                  console.log("🎤 INTERRUPTION TRIGGERED!", {
                    transcript: transcript.trim(),
                    length: transcript.trim().length,
                    isFinal,
                    triggeredBy: isLLMProcessing
                      ? "LLM Processing"
                      : "Avatar Speaking",
                    timestamp: Date.now(),
                  });

                  // Mark that we've triggered interruption for this speech session
                  hasTriggeredInterruptionThisSession = true;
                  resetSpeechSpeculation(store, "interruption_started");

                  // Start interruption collection
                  store.dispatch(speechActions.startInterruption());

                  // Interrupt avatar if it's speaking
                  if (isAvatarSpeaking) {
                    console.log("Interrupting Avatar Speech: ", Date.now());
                    console.log("🛑 Interrupting avatar speech...");
                    store.dispatch({
                      type: "avatar/interrupt",
                      payload: { reason: "user_interruption_interim" },
                    });
                  }

                  // Send interruption-start to backend with context
                  store.dispatch({
                    type: "websocket/sendMessage",
                    payload: {
                      event: "interruption-start",
                      data: {
                        sessionId: currentState.sessions.current?.sessionId,
                        interruptionContext: {
                          wasLLMProcessing: isLLMProcessing,
                          wasAvatarSpeaking: isAvatarSpeaking,
                          timestamp: Date.now(),
                          triggerType: isFinal
                            ? "final_result"
                            : "interim_result",
                          triggerLength: transcript.trim().length,
                        },
                      },
                    },
                  });

                  // Send the transcript as first interruption chunk
                  store.dispatch({
                    type: "websocket/sendMessage",
                    payload: {
                      event: "interruption-chunk",
                      data: {
                        sessionId: currentState.sessions.current?.sessionId,
                        chunk: transcript.trim(),
                      },
                    },
                  });
                }
              }

              // FINAL RESULT PROCESSING - Only if not already handled by interruption
              if (isFinal && transcript.trim().length > 2) {
                const currentState = store.getState();

                if (currentState.speech.isCollectingInterruption) {
                  // We're already collecting interruption - add this final result as a chunk
                  console.log(
                    "🎤 Adding final result to interruption chunks:",
                    transcript,
                  );
                  store.dispatch({
                    type: "websocket/sendMessage",
                    payload: {
                      event: "interruption-chunk",
                      data: {
                        sessionId: currentState.sessions.current?.sessionId,
                        chunk: transcript.trim(),
                      },
                    },
                  });
                } else if (!hasTriggeredInterruptionThisSession) {
                  // No interruption triggered yet - either start interruption OR queue normally
                  const isLLMProcessing = currentState.messages.isProcessing;
                  const isAvatarSpeaking =
                    currentState.avatar.isSpeaking &&
                    currentState.avatar.isConnected;
                  const isAlreadyInterrupting =
                    currentState.speech.isCollectingInterruption;
                  const isAlreadyInterrupted =
                    currentState.messages.isInterrupted;

                  const shouldInterrupt =
                    (isLLMProcessing || isAvatarSpeaking) &&
                    !isAlreadyInterrupting &&
                    !isAlreadyInterrupted;

                  if (shouldInterrupt) {
                    // Fallback interruption trigger (shouldn't happen often with interim logic)
                    console.log(
                      "🎤 FINAL RESULT INTERRUPTION (fallback):",
                      transcript,
                    );
                    hasTriggeredInterruptionThisSession = true;
                    resetSpeechSpeculation(store, "interruption_started");

                    store.dispatch(speechActions.startInterruption());

                    if (isAvatarSpeaking) {
                      console.log(
                        "🛑 Interrupting avatar speech (final fallback)...",
                      );
                      store.dispatch({
                        type: "avatar/interrupt",
                        payload: { reason: "user_interruption_final" },
                      });
                    }

                    store.dispatch({
                      type: "websocket/sendMessage",
                      payload: {
                        event: "interruption-start",
                        data: {
                          sessionId: currentState.sessions.current?.sessionId,
                          interruptionContext: {
                            wasLLMProcessing: isLLMProcessing,
                            wasAvatarSpeaking: isAvatarSpeaking,
                            timestamp: Date.now(),
                            triggerType: "final_result_fallback",
                          },
                        },
                      },
                    });

                    store.dispatch({
                      type: "websocket/sendMessage",
                      payload: {
                        event: "interruption-chunk",
                        data: {
                          sessionId: currentState.sessions.current?.sessionId,
                          chunk: transcript.trim(),
                        },
                      },
                    });
                  } else {
                    // Normal queuing (no interruption needed)
                    console.log(
                      "🎤 Adding transcript to normal queue:",
                      transcript,
                    );
                    store.dispatch(
                      speechActions.addToTranscriptQueue(transcript.trim()),
                    );
                  }
                }
              }
            },
            // Error callback
            (error: Error) => {
              console.error("🎤 Speech error:", error);
              store.dispatch(speechActions.setError(error.message));
              store.dispatch(speechActions.setConnecting(false));
            },
            // Utterance-end callback
            () => {
              console.log("🎤 Deepgram utterance ended");
              store.dispatch({ type: "speech/utteranceEnd" });
            },
            // Speech-start callback
            () => {
              console.log("🎤 Deepgram speech started");
              maybeSendSpeechStartWarmup(store);
            },
            // Device ID
            currentState.speech.selectedDeviceId,
          );

          console.log("🎤 Speech connected successfully");
          store.dispatch(speechActions.setConnecting(false));
          store.dispatch(speechActions.setListening(true));
        } catch (error: any) {
          console.error("🎤 Failed to start speech:", error);
          store.dispatch(speechActions.setError(error.message));
          store.dispatch(speechActions.setConnecting(false));
        }
      }, 100);

      return result;
    }

    // Handle toggle action - SIMPLIFIED (no duplicate transcript callback)
    if (type === "speech/toggle") {
      const state = store.getState();

      console.log("🎤 Speech toggle middleware:", {
        enabled: state.speech.isEnabled,
        listening: state.speech.isListening,
        connecting: state.speech.isConnecting,
      });

      // Block if not enabled or connecting
      if (!state.speech.isEnabled || state.speech.isConnecting) {
        console.log("🎤 Toggle blocked");
        return result;
      }

      if (state.speech.isListening) {
        // Stop listening
        console.log("🎤 Stopping speech...");
        store.dispatch(speechActions.setListening(false));
        deepgramService.disconnect();
      } else {
        // Start listening by delegating to connectSpeech
        console.log("🎤 Starting speech via connectSpeech...");
        store.dispatch({ type: "speech/connectSpeech" });
      }
    }

    // Handle utterance end with flag cleanup
    if (type === "speech/utteranceEnd") {
      console.log(
        "🎤 Utterance ended - processing queued transcripts and resetting interruption flag",
      );
      const state = store.getState();

      // RESET the interruption trigger flag for next speech session
      console.log("🔄 Resetting hasTriggeredInterruptionThisSession flag");
      hasTriggeredInterruptionThisSession = false;

      if (state.speech.isCollectingInterruption) {
        // End interruption
        console.log("🎤 Ending interruption collection and sending to backend");
        store.dispatch(speechActions.endInterruption());

        // Send interruption-end to backend
        store.dispatch({
          type: "websocket/sendMessage",
          payload: {
            event: "interruption-end",
            data: {
              sessionId: state.sessions.current?.sessionId,
            },
          },
        });
      } else {
        // Normal transcript processing (no interruption)
        const queuedTranscripts = state.speech.transcriptQueue;

        if (queuedTranscripts.length > 0) {
          const fullTranscript = queuedTranscripts.join(" ").trim();
          const currentSession = state.sessions.current;

          if (
            currentSession &&
            !state.messages.isProcessing &&
            fullTranscript.length > 2
          ) {
            const inputCompletedAt = performance.now();
            console.log("🎤 Sending combined transcript:", fullTranscript);
            store.dispatch({
              type: "websocket/askQuestion",
              payload: {
                question: fullTranscript,
                caseId: currentSession.caseId,
                sessionId: currentSession.sessionId,
                timing: buildSpeechExchangeTiming(inputCompletedAt),
              },
            });

            store.dispatch(speechActions.clearTranscriptQueue());
            clearLocalSpeechSpeculation();
            resetSpeechExchangeTiming();
          } else {
            resetSpeechSpeculation(store, "speech_not_sent");
            resetSpeechExchangeTiming();
          }
        } else {
          resetSpeechSpeculation(store, "empty_utterance");
          resetSpeechExchangeTiming();
        }
      }
    }

    // Flag reset handlers
    if (type === "speech/setListening") {
      const { payload } = action as { payload: boolean };
      if (!payload) {
        // Reset flag when stopping speech recognition
        console.log("🔄 Speech stopped - resetting interruption trigger flag");
        hasTriggeredInterruptionThisSession = false;
        resetSpeechSpeculation(store, "speech_stopped");
        resetSpeechExchangeTiming();
      }
    }

    // Reset flag when ending interruption collection
    if (type === "speech/endInterruption") {
      console.log("🔄 Interruption ended - resetting trigger flag");
      hasTriggeredInterruptionThisSession = false;
    }

    // Reset flag when speech recognition resets
    if (type === "speech/reset") {
      console.log("🔄 Speech reset - resetting interruption trigger flag");
      hasTriggeredInterruptionThisSession = false;
      resetSpeechSpeculation(store, "speech_reset");
      resetSpeechExchangeTiming();
    }

    return result;
  };
